// ============================================
// JOB CREATE PAGE
// ============================================

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Loader2, Save, Search } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { PageHeader } from '@/components';
import { useCreateJob } from '@/hooks';
import { departmentLabels, vesselTypeLabels, countries } from '@/utils';
import { commonApi } from '@/services/api';
import { useState } from 'react';

export const Route = createFileRoute('/_authenticated/jobs/create')({
  component: JobCreatePage,
});

const jobSchema = z.object({
  title: z.string().min(5, 'Başlık en az 5 karakter olmalıdır'),
  description: z.string().min(50, 'Açıklama en az 50 karakter olmalıdır'),
  department: z.string().optional(),
  rankId: z.string().optional(),
  vesselType: z.string().min(1, 'Gemi tipi seçiniz'),
  vesselName: z.string().optional(),
  vesselDWT: z.preprocess((val) => (val === '' || val === undefined || Number.isNaN(val) ? undefined : Number(val)), z.number().min(0).optional()),
  vesselBuiltYear: z.preprocess((val) => (val === '' || val === undefined || Number.isNaN(val) ? undefined : Number(val)), z.number().min(1900).max(2030).optional()),
  engineBrand: z.string().optional(),
  engineKw: z.preprocess((val) => (val === '' || val === undefined || Number.isNaN(val) ? undefined : Number(val)), z.number().min(0).optional()),
  hasEcdis: z.boolean().optional(),
  hasSatelliteInternet: z.boolean().optional(),
  preferredNationality: z.string().optional(),
  crewCount: z.preprocess((val) => (val === '' || val === undefined || Number.isNaN(val) ? undefined : Number(val)), z.number().min(1).optional()),
  salaryMin: z.preprocess((val) => (val === '' || val === undefined || Number.isNaN(val) ? undefined : Number(val)), z.number().min(0, 'Geçerli bir maaş giriniz').optional()),
  salaryMax: z.preprocess((val) => (val === '' || val === undefined || Number.isNaN(val) ? undefined : Number(val)), z.number().min(0, 'Geçerli bir maaş giriniz').optional()),
  salaryCurrency: z.string().default('USD'),
  contractDuration: z.preprocess((val) => (val === '' || val === undefined || Number.isNaN(val) ? 6 : Number(val)), z.number().min(1, 'Kontrat süresi giriniz')),
  experienceMin: z.preprocess((val) => (val === '' || val === undefined || Number.isNaN(val) ? 0 : Number(val)), z.number().min(0)),
  joinDate: z.string().optional(),
  expiresAt: z.string().optional(),
});

type JobFormData = z.infer<typeof jobSchema>;

function JobCreatePage() {
  const navigate = useNavigate();
  const createJobMutation = useCreateJob();

  // Fetch ranks for dropdown - tüm rankları çek
  const { data: rawRanksData, isLoading: ranksLoading } = useQuery({
    queryKey: ['ranks', 'all'],
    queryFn: () => commonApi.getRanks(),
    staleTime: 10 * 60 * 1000, // 10 dakika cache'le
    gcTime: 30 * 60 * 1000, // 30 dakika garbage collection
  });

  // Handle nested response for ranks: { success, data: [...] } or { data: [...] } or direct array
  const ranksList: any[] = (() => {
    if (!rawRanksData) return [];
    
    // Case 1: Direct array
    if (Array.isArray(rawRanksData)) return rawRanksData;
    
    // Case 2: { data: [...] } or { success: true, data: [...] }
    const response = rawRanksData as any;
    if (response.data) {
      // Could be { data: { data: [...] } } from axios wrapper
      if (Array.isArray(response.data)) return response.data;
      if (response.data.data && Array.isArray(response.data.data)) return response.data.data;
    }
    
    return [];
  })();

  // Sertifika tiplerini çek
  const { data: rawCertData } = useQuery({
    queryKey: ['certificate-types'],
    queryFn: async () => {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1'}/common/certificate-types`);
      return response.json();
    },
    staleTime: 10 * 60 * 1000,
  });

  const certTypesList: any[] = (() => {
    if (!rawCertData) return [];
    if (Array.isArray(rawCertData)) return rawCertData;
    const resp = rawCertData as any;
    if (resp.data) {
      if (Array.isArray(resp.data)) return resp.data;
      if (resp.data.data && Array.isArray(resp.data.data)) return resp.data.data;
    }
    return [];
  })();

  // Seçili vatandaşlık kodları (max 3)
  const [selectedNationalities, setSelectedNationalities] = useState<string[]>([]);
  const [natSearch, setNatSearch] = useState('');

  const filteredCountries = countries.filter((c) => {
    if (!natSearch) return true;
    return c.name.toLowerCase().includes(natSearch.toLowerCase()) || c.code.toLowerCase().includes(natSearch.toLowerCase());
  });

  const toggleNationality = (code: string) => {
    setSelectedNationalities(prev => {
      if (prev.includes(code)) return prev.filter(c => c !== code);
      if (prev.length >= 3) return prev; // max 3
      return [...prev, code];
    });
  };

  // Seçili sertifika ID'leri
  const [selectedCertificates, setSelectedCertificates] = useState<string[]>([]);
  const [certSearch, setCertSearch] = useState('');

  const filteredCertTypes = certTypesList.filter((ct: any) => {
    if (!certSearch) return true;
    const search = certSearch.toLowerCase();
    return (ct.nameTr?.toLowerCase().includes(search) || ct.nameEn?.toLowerCase().includes(search) || ct.code?.toLowerCase().includes(search));
  });

  const toggleCertificate = (id: string) => {
    setSelectedCertificates(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<JobFormData>({
    resolver: zodResolver(jobSchema),
    defaultValues: {
      salaryCurrency: 'USD',
      experienceMin: 0,
      contractDuration: 6,
    },
  });

  const selectedDepartment = watch('department');

  const onSubmit = async (data: JobFormData) => {
    try {
      const deckRanks = ranksList.filter((r: any) => r.department === 'DECK');
      const defaultRankId = deckRanks[0]?.id || ranksList[0]?.id || '';

      // Clean data - remove undefined/NaN values and prepare for API
      const cleanData: Record<string, unknown> = {
        title: data.title,
        description: data.description,
        department: 'DECK',
        rankId: defaultRankId,
        vesselType: data.vesselType,
        salaryCurrency: data.salaryCurrency || 'USD',
      };
      
      // Optional string fields
      if (data.vesselName) cleanData.vesselName = data.vesselName;
      if (data.engineBrand) cleanData.engineBrand = data.engineBrand;
      if (selectedNationalities.length > 0) cleanData.preferredNationality = selectedNationalities.join(',');
      if (data.joinDate) cleanData.joinDate = data.joinDate;
      if (data.expiresAt) cleanData.expiresAt = data.expiresAt;
      
      // Optional number fields - only include if valid
      if (typeof data.vesselDWT === 'number' && !Number.isNaN(data.vesselDWT)) {
        cleanData.vesselDWT = data.vesselDWT;
      }
      if (typeof data.vesselBuiltYear === 'number' && !Number.isNaN(data.vesselBuiltYear)) {
        cleanData.vesselBuiltYear = data.vesselBuiltYear;
      }
      if (typeof data.engineKw === 'number' && !Number.isNaN(data.engineKw)) {
        cleanData.engineKw = data.engineKw;
      }
      if (typeof data.crewCount === 'number' && !Number.isNaN(data.crewCount)) {
        cleanData.crewCount = data.crewCount;
      }
      if (typeof data.salaryMin === 'number' && !Number.isNaN(data.salaryMin)) {
        cleanData.salaryMin = data.salaryMin;
      }
      if (typeof data.salaryMax === 'number' && !Number.isNaN(data.salaryMax)) {
        cleanData.salaryMax = data.salaryMax;
      }
      if (typeof data.experienceMin === 'number' && !Number.isNaN(data.experienceMin)) {
        cleanData.experienceMin = data.experienceMin;
      }
      if (typeof data.contractDuration === 'number' && !Number.isNaN(data.contractDuration)) {
        cleanData.contractDuration = data.contractDuration;
      }
      
      // Boolean fields
      if (data.hasEcdis !== undefined) cleanData.hasEcdis = data.hasEcdis;
      if (data.hasSatelliteInternet !== undefined) cleanData.hasSatelliteInternet = data.hasSatelliteInternet;
      
      // Gerekli sertifikalar - ID dizisi olarak gönder
      if (selectedCertificates.length > 0) {
        cleanData.requirements = selectedCertificates;
      }
      
      await createJobMutation.mutateAsync(cleanData);
      navigate({ to: '/jobs' });
    } catch (error) {
      console.error('Job creation failed:', error);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Yeni İş İlanı"
        description="Yeni bir iş ilanı oluşturun"
        actions={
          <Link to="/jobs" className="btn-secondary">
            <ArrowLeft className="w-4 h-4" />
            Geri
          </Link>
        }
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Info */}
        <div className="bg-white rounded-maritime border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Temel Bilgiler</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Title */}
            <div className="md:col-span-2">
              <label className="label">İlan Başlığı *</label>
              <input
                type="text"
                className={`input ${errors.title ? 'input-error' : ''}`}
                placeholder="örn: Chief Officer - Tanker"
                {...register('title')}
              />
              {errors.title && (
                <p className="mt-1 text-xs text-red-500">{errors.title.message}</p>
              )}
            </div>



            {/* Vessel Type */}
            <div>
              <label className="label">Gemi Tipi *</label>
              <select
                className={`input ${errors.vesselType ? 'input-error' : ''}`}
                {...register('vesselType')}
              >
                <option value="">Seçiniz</option>
                {Object.entries(vesselTypeLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              {errors.vesselType && (
                <p className="mt-1 text-xs text-red-500">{errors.vesselType.message}</p>
              )}
            </div>

            {/* Vessel Name */}
            <div>
              <label className="label">Gemi Adı</label>
              <input
                type="text"
                className="input"
                placeholder="örn: MV Ocean Star"
                {...register('vesselName')}
              />
            </div>

            {/* Vessel DWT */}
            <div>
              <label className="label">Gemi DWT</label>
              <input
                type="number"
                className="input"
                placeholder="örn: 50000"
                {...register('vesselDWT', { valueAsNumber: true })}
              />
            </div>

            {/* Vessel Built Year */}
            <div>
              <label className="label">Gemi İnşa Yılı</label>
              <input
                type="number"
                className="input"
                placeholder="örn: 2018"
                {...register('vesselBuiltYear', { valueAsNumber: true })}
              />
            </div>

            {/* Engine Brand */}
            <div>
              <label className="label">Ana Makine Markası</label>
              <input
                type="text"
                className="input"
                placeholder="örn: MAN B&W"
                {...register('engineBrand')}
              />
            </div>

            {/* Engine KW */}
            <div>
              <label className="label">Ana Makine KW</label>
              <input
                type="number"
                className="input"
                placeholder="örn: 15000"
                {...register('engineKw', { valueAsNumber: true })}
              />
            </div>

            {/* Has ECDIS */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="hasEcdis"
                className="w-4 h-4 text-maritime-600 rounded"
                {...register('hasEcdis')}
              />
              <label htmlFor="hasEcdis" className="text-sm text-gray-700">ECDIS Mevcut</label>
            </div>

            {/* Has Satellite Internet */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="hasSatelliteInternet"
                className="w-4 h-4 text-maritime-600 rounded"
                {...register('hasSatelliteInternet')}
              />
              <label htmlFor="hasSatelliteInternet" className="text-sm text-gray-700">Uydu İnterneti Mevcut</label>
            </div>

            {/* Preferred Nationality - Multi-select, max 3 */}
            <div>
              <label className="label">
                Tercih Edilen Vatandaşlık
                <span className="ml-1 text-xs text-gray-400">(En fazla 3 seçim)</span>
              </label>

              {/* Seçili vatandaşlıklar - tag */}
              {selectedNationalities.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {selectedNationalities.map(code => {
                    const country = countries.find(c => c.code === code);
                    return country ? (
                      <span key={code} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                        {country.name}
                        <button type="button" onClick={() => toggleNationality(code)} className="ml-1 text-blue-400 hover:text-blue-600">&times;</button>
                      </span>
                    ) : null;
                  })}
                </div>
              )}

              {/* Arama */}
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  className="input pl-10"
                  placeholder="Ülke ara..."
                  value={natSearch}
                  onChange={e => setNatSearch(e.target.value)}
                />
              </div>

              {/* Ülke listesi */}
              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {filteredCountries.map((country) => {
                  const isSelected = selectedNationalities.includes(country.code);
                  const isDisabled = !isSelected && selectedNationalities.length >= 3;
                  return (
                    <label key={country.code} className={`flex items-center gap-3 px-3 py-2 cursor-pointer ${isDisabled ? 'opacity-40' : 'hover:bg-gray-50'}`}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleNationality(country.code)}
                        disabled={isDisabled}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{country.name}</span>
                    </label>
                  );
                })}
              </div>
              <p className="mt-1 text-xs text-gray-500">{selectedNationalities.length}/3 ülke seçildi</p>
            </div>

            {/* Crew Count */}
            <div>
              <label className="label">Aranan Personel Sayısı</label>
              <input
                type="number"
                className="input"
                placeholder="örn: 2"
                min="1"
                {...register('crewCount', { valueAsNumber: true })}
              />
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <label className="label">Açıklama *</label>
              <textarea
                className={`input min-h-[150px] ${errors.description ? 'input-error' : ''}`}
                placeholder="İş tanımı, sorumluluklar ve beklentiler..."
                {...register('description')}
              />
              {errors.description && (
                <p className="mt-1 text-xs text-red-500">{errors.description.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Salary & Contract */}
        <div className="bg-white rounded-maritime border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Maaş & Kontrat</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Salary Min */}
            <div>
              <label className="label">Minimum Maaş</label>
              <input
                type="number"
                className={`input ${errors.salaryMin ? 'input-error' : ''}`}
                placeholder="5000"
                {...register('salaryMin', { valueAsNumber: true })}
              />
              {errors.salaryMin && (
                <p className="mt-1 text-xs text-red-500">{errors.salaryMin.message}</p>
              )}
            </div>

            {/* Salary Max */}
            <div>
              <label className="label">Maksimum Maaş</label>
              <input
                type="number"
                className={`input ${errors.salaryMax ? 'input-error' : ''}`}
                placeholder="7000"
                {...register('salaryMax', { valueAsNumber: true })}
              />
              {errors.salaryMax && (
                <p className="mt-1 text-xs text-red-500">{errors.salaryMax.message}</p>
              )}
            </div>

            {/* Currency */}
            <div>
              <label className="label">Para Birimi</label>
              <select className="input" {...register('salaryCurrency')}>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="TRY">TRY</option>
              </select>
            </div>

            {/* Contract Duration */}
            <div>
              <label className="label">Kontrat Süresi (Ay) *</label>
              <input
                type="number"
                className={`input ${errors.contractDuration ? 'input-error' : ''}`}
                placeholder="6"
                {...register('contractDuration', { valueAsNumber: true })}
              />
              {errors.contractDuration && (
                <p className="mt-1 text-xs text-red-500">{errors.contractDuration.message}</p>
              )}
            </div>

            {/* Experience Min */}
            <div>
              <label className="label">Minimum Deneyim (Yıl)</label>
              <input
                type="number"
                className="input"
                placeholder="0"
                {...register('experienceMin', { valueAsNumber: true })}
              />
            </div>

            {/* Join Date */}
            <div>
              <label className="label">Başlama Tarihi</label>
              <input
                type="date"
                className="input"
                {...register('joinDate')}
              />
            </div>

            {/* Expiration Date */}
            <div>
              <label className="label">İlan Bitiş Tarihi</label>
              <input
                type="date"
                className="input"
                {...register('expiresAt')}
              />
            </div>
          </div>
        </div>

        {/* Gerekli Sertifikalar */}
        <div className="bg-white rounded-maritime border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Gerekli Sertifikalar</h3>
          
          <div>
            <label className="label">Sertifika Gereksinimleri (Opsiyonel)</label>
            
            {/* Seçili sertifikalar */}
            {selectedCertificates.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {selectedCertificates.map(certId => {
                  const cert = certTypesList.find((c: any) => c.id === certId);
                  return cert ? (
                    <span key={certId} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                      {cert.nameTr || cert.nameEn}
                      <button type="button" onClick={() => toggleCertificate(certId)} className="ml-1 text-blue-400 hover:text-blue-600">&times;</button>
                    </span>
                  ) : null;
                })}
              </div>
            )}

            {/* Arama */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                className="input pl-10"
                placeholder="Sertifika ara..."
                value={certSearch}
                onChange={e => setCertSearch(e.target.value)}
              />
            </div>

            {/* Sertifika listesi */}
            <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
              {filteredCertTypes.length === 0 ? (
                <p className="p-3 text-sm text-gray-500 text-center">Sertifika bulunamadı</p>
              ) : (
                filteredCertTypes.map((cert: any) => (
                  <label key={cert.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedCertificates.includes(cert.id)}
                      onChange={() => toggleCertificate(cert.id)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-700 truncate">{cert.nameTr || cert.nameEn}</p>
                      {cert.code && <p className="text-xs text-gray-400">{cert.code}</p>}
                    </div>
                  </label>
                ))
              )}
            </div>
            <p className="mt-2 text-xs text-gray-500">
              {selectedCertificates.length} sertifika seçildi
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <Link to="/jobs" className="btn-secondary">
            İptal
          </Link>
          <button
            type="submit"
            className="btn-primary"
            disabled={isSubmitting || createJobMutation.isPending}
          >
            {(isSubmitting || createJobMutation.isPending) ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Kaydediliyor...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                İlanı Yayınla
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
